import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import claimJobDocumentDeleteApi from 'models/claims/claimJobDocument/delete/Api.ClaimJobDocument.Delete';
import claimJobDocumentSaveApi from 'models/claims/claimJobDocument/save/Api.ClaimJobDocument.Save';
import ClaimTypeDocument from 'models/claims/claimTypeDocument/Model.ClaimTypeDocument';
import ClaimDocumentPreview from 'module/claims/views/claim/other/Claim.DocumentPreview';
import { useMemo } from 'react';
import { useClaimJobEdit } from './ClaimEdit.Job.Hooks';

type DeleteJobDocumentProps = { documentId: number; claimJobId: number };
function OneDocForm(props: { claimTypeDocument: ClaimTypeDocument }) {
  const { lang } = getLocales();
  const { reload, uploadedDocuments, claimJob, isClaimJobEditableByStatus, permissions, isClaimAdmin, isClaimOwner } =
    useClaimJobEdit();

  const docForm = useForm<Utils.FormData<Model.IClaimJobDocument>>({
    default: {
      claimJobId: claimJob.id,
      claimTypeDocumentId: props.claimTypeDocument.id,
      approved: !props.claimTypeDocument.approvalRequired,
    },
    onSubmit: async (data) => {
      if (data.formFile) {
        const res = await claimJobDocumentSaveApi(data);
        if (res.status !== HttpStatus.ok) {
          addLog({
            error: 'We were unable to upload document',
          });
        }
      }
      docForm.reload();
      reload();
    },
    validation: (data, errors) => {
      if (!data.name) errors.name = lang.required;
      if (!data.formFile)
        errors.formFile = {
          _objectError: lang.required,
        };
    },
  });

  const uploadedFile = useMemo(
    () =>
      !docForm.data?.formFile
        ? null
        : {
            original: docForm.data.formFile,
            type: docForm.data.formFile.type as Utils.File.MimeType,
            dataUrl: window.URL.createObjectURL(docForm.data.formFile),
            name: docForm.data.name,
            notes: docForm.data.notes,
          },
    [docForm.data.formFile]
  );

  const hasUploadedDocs = uploadedDocuments?.some((it) => it.claimTypeDocumentId === props.claimTypeDocument.id);
  const allowMoreUploads = !hasUploadedDocs ? true : !!props.claimTypeDocument.allowMultiple;

  return (
    <InputWrapper style={{ marginBottom: Theme.Size.L }}>
      <FileInputSimple
        name="file"
        value={uploadedFile}
        onChange={({ file }) => {
          docForm.update({
            formFile: file.original,
            name: file.original?.name,
          });
        }}
        label={props.claimTypeDocument.name}
        error={docForm.errors?.formFile?._objectError}
        disabled={
          !allowMoreUploads ||
          isClaimJobEditableByStatus ||
          !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMJOBDOCUMENT))
        }
      />
      <TextInput
        name="name"
        label={lang.name}
        value={docForm.data?.name}
        error={docForm.errors?.name}
        onChange={docForm.update}
        disabled={
          !uploadedFile ||
          isClaimJobEditableByStatus ||
          !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMJOBDOCUMENT))
        }
      />
      {props.claimTypeDocument?.allowNotes && (
        <TextInput
          name="notes"
          label={lang.notes}
          value={docForm.data?.notes}
          onChange={docForm.update}
          disabled={
            !uploadedFile ||
            isClaimJobEditableByStatus ||
            !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMJOBDOCUMENT))
          }
        />
      )}
      <Button
        render={lang.upload}
        onClick={docForm.submit}
        disabled={
          docForm.hasErrors ||
          isClaimJobEditableByStatus ||
          !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMJOBDOCUMENT)) /*||
            (isEmpty(uploadedDocuments) &&
              uploadableDocuments.some((doc) => doc.required && isEmpty(documentsFiles[doc.id])))*/
        }
        active={!docForm.hasErrors}
        class={ThemeButtonCircle}
        style={{ flexShrink: 0 }}
      />
    </InputWrapper>
  );
}
export default function ClaimEditJobDocuments() {
  const { lang } = getLocales();
  const { reload, uploadedDocuments, uploadableDocuments, permissions, isClaimAdmin, isClaimOwner } = useClaimJobEdit();
  const { execute: deleteClaimJobDocument } = useApi({
    action: claimJobDocumentDeleteApi,
    wait: true,
    callback: reload,
  });

  const onJobDocumentDelete = ({ documentId, claimJobId }: DeleteJobDocumentProps) => {
    deleteClaimJobDocument({ claimJobId, documentId });
    window.location.reload();
  };

  //#region Document form editing
  //#endregion

  return !uploadableDocuments.length ? null : (
    <>
      <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
        <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>{lang.documents}</h2>
        <br />
        <DataMap
          data={uploadableDocuments}
          render={({ data: doc }) => <OneDocForm key={doc.id} claimTypeDocument={doc} />}
        />
        <br />
      </div>
      <ClaimDocumentPreview
        showGallery={true}
        documents={uploadedDocuments.map((it) => ({
          id: it.id,
          url: it.mediaFile?.url,
          name: it.name,
          extension: it.mediaFile?.extension,
          mimeType: it.mediaFile?.contentType,
          storedFileName: it.mediaFile?.name,
          description: it.notes,
          approved: it.approved,
          documentType: lang.document,
          documentName: uploadableDocuments.find((ud) => ud.id === it.claimTypeDocumentId)?.name,
          onDelete: () => onJobDocumentDelete({ documentId: it.id, claimJobId: it.claimJobId }),
        }))}
        isDeleteDisabled={!(isClaimAdmin || (isClaimOwner && permissions?.DELETE_CLAIMJOBDOCUMENT))}
        isDownloadDisabled={!(isClaimAdmin || (isClaimOwner && permissions?.DOWNLOAD_CLAIMJOBDOCUMENT))}
      />
      <hr />
    </>
  );
}
